import React, { useState, useRef } from 'react';
import { 
  CloudArrowUpIcon, 
  DocumentIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import toast from '../utils/toast';

const TrademarkDocumentUpload = ({ trademarkIndex, trademarkType, documents, onUpload, onDocumentUrlReceived }) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const getRequiredDocuments = () => {
    switch (trademarkType) {
      case 'Color Mark (Device)':
        return ['Trademark Image'];
      case 'Logo Mark (Device)':
        return ['Trademark Logo'];
      case 'Shape':
        return ['Trademark Shape'];
      case 'Sound':
        return ['Trademark Sound File'];
      default:
        return [];
    }
  };

  const getDocumentDisplayName = (documentType) => {
    const displayNames = {
      'Trademark Image': 'Trademark Image',
      'Trademark Logo': 'Trademark Logo', 
      'Trademark Shape': 'Trademark Shape',
      'Trademark Sound File': 'Trademark Sound File',
      'UDYOG_AADHAAR': 'Udyog Aadhaar Certificate',
      'STARTUP_CERTIFICATE': 'Startup Certificate',
      'POWER_OF_ATTORNEY': 'Power of Attorney'
    };
    return displayNames[documentType] || documentType;
  };

  const validateFile = (file) => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = [
      'image/jpeg',
      'image/jpg'
    ];

    if (file.size > maxSize) {
      toast.error('File size must be less than 5MB');
      return false;
    }

    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG or JPEG files only.');
      return false;
    }

    return true;
  };

  // Function to resize image to 500x500 or smaller
  const resizeImage = (file) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;
        const maxDimension = 500;
        
        if (width > height) {
          if (width > maxDimension) {
            height = (height * maxDimension) / width;
            width = maxDimension;
          }
        } else {
          if (height > maxDimension) {
            width = (width * maxDimension) / height;
            height = maxDimension;
          }
        }
        
        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;
        
        // Draw resized image
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to blob
        canvas.toBlob((blob) => {
          // Create new file with resized image
          const resizedFile = new File([blob], file.name, {
            type: 'image/jpeg',
            lastModified: Date.now()
          });
          resolve(resizedFile);
        }, 'image/jpeg', 0.9); // 90% quality
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (file) => {
    if (!validateFile(file)) {
      return;
    }

    try {
      setUploading(true);
      
      // Resize image if it's an image file
      let processedFile = file;
      if (file.type.startsWith('image/')) {
        processedFile = await resizeImage(file);
      }
      
      // First, upload the file to get the URL
      const formData = new FormData();
      formData.append('file', processedFile);
      
      // Use public upload endpoint if application doesn't have an ID yet
      const uploadEndpoint = '/upload/public';
      
      const response = await api.post(uploadEndpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (!response.data.success) {
        throw new Error('Upload failed');
      }
      
      const documentUrl = response.data.url;
      
      // Determine document type based on trademark type
      let documentType = 'OTHER';
      let trademarkDocumentType = 'Other Document';
      
      switch (trademarkType) {
        case 'Color Mark (Device)':
          documentType = 'TRADEMARK_IMAGE';
          trademarkDocumentType = 'Trademark Image';
          break;
        case 'Logo Mark (Device)':
          documentType = 'TRADEMARK_LOGO';
          trademarkDocumentType = 'Trademark Logo';
          break;
        case 'Shape':
          documentType = 'TRADEMARK_SHAPE';
          trademarkDocumentType = 'Trademark Shape';
          break;
        case 'Sound':
          documentType = 'TRADEMARK_SOUND';
          trademarkDocumentType = 'Trademark Sound File';
          break;
        default:
          documentType = 'OTHER';
          trademarkDocumentType = 'Other Document';
      }

      // Call the callback to store the document URL locally
      onDocumentUrlReceived({
        documentType,
        trademarkIndex,
        trademarkDocumentType,
        documentUrl,
        fileName: processedFile.name,
        fileSize: processedFile.size,
        mimeType: processedFile.type
      });

      toast.success('Document uploaded successfully');

      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const removeDocument = (document) => {
    // This would need to be implemented in the parent component
    // For now, we'll just show a message
    toast.info('Document removal functionality will be implemented');
  };

  const renderUploadArea = () => {
    const requiredDocuments = getRequiredDocuments();
    
    return (
      <div className="space-y-4">
        {requiredDocuments.map((docType) => {
          const existingDoc = documents.find(doc => doc.type === docType);
          
          return (
            <div key={docType} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h5 className="text-sm font-medium text-gray-900">
                  {docType} *
                </h5>
                {existingDoc && existingDoc.uploaded && (
                  <div className="flex items-center text-green-600">
                    <CheckCircleIcon className="h-4 w-4 mr-1" />
                    <span className="text-xs">Uploaded</span>
                  </div>
                )}
              </div>

              {existingDoc && existingDoc.uploaded ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <DocumentIcon className="h-5 w-5 text-green-500 mr-2" />
                      <div>
                        <p className="text-sm font-medium text-green-900">
                          {existingDoc.fileName || 'Document uploaded'}
                        </p>
                        <p className="text-xs text-green-700">
                          Uploaded successfully
                        </p>
                        {existingDoc.documentUrl && (
                          <a
                            href={existingDoc.documentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            View Document
                          </a>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeDocument(existingDoc)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className={`border-2 border-dashed rounded-lg p-3 text-center ${
                    dragActive 
                      ? 'border-blue-400 bg-blue-50' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <CloudArrowUpIcon className="mx-auto h-6 w-6 text-gray-400" />
                  <div className="mt-1">
                    <label
                      htmlFor={`file-upload-${docType}`}
                      className="cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                    >
                      <span>Upload a file</span>
                      <input
                        id={`file-upload-${docType}`}
                        ref={fileInputRef}
                        type="file"
                        className="sr-only"
                        onChange={handleFileInput}
                        accept="image/jpeg,image/jpg"
                        disabled={uploading}
                      />
                    </label>
                    <p className="text-xs text-gray-500">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    JPG or JPEG only, max 500x500 pixels, up to 5MB
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Upload Areas */}
      {renderUploadArea()}

      {/* Upload Progress */}
      {uploading && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600 mr-2"></div>
            <span className="text-sm text-yellow-800">Uploading document...</span>
          </div>
        </div>
      )}

      {/* Help Text */}
      {/* <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-start">
          <ExclamationTriangleIcon className="h-5 w-5 text-gray-400 mt-0.5 mr-2" />
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-1">Important Notes</h4>
            <ul className="text-xs text-gray-600 space-y-1">
              <li>• Only JPG or JPEG files are accepted</li>
              <li>• Images will be automatically resized to max 500x500 pixels</li>
              <li>• Maximum file size: 5MB</li>
              <li>• All documents must be clear and legible</li>
              <li>• Ensure file names are descriptive</li>
              <li>• Keep original files for future reference</li>
              <li>• Documents will be validated by our team</li>
            </ul>
          </div>
        </div>
      </div> */}
    </div>
  );
};

export default TrademarkDocumentUpload; 